import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_OFFSET = 14;
const EDGE_PADDING = 12;
const ARROW_SAFE_PADDING = 28;
const MOBILE_BREAKPOINT = 640;

export default function FeatureTooltip({
  targetRef,
  open = false,
  title,
  description,
  buttonLabel = 'Got it',
  secondaryButtonLabel,
  placement = 'right', // top | right | bottom | left
  align = 'center', // start | center | end
  offset = DEFAULT_OFFSET,
  maxWidth = 420,
  zIndex = 3000,
  onClose,
  onSecondaryClick,
  className = '',
}) {
  const tooltipRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [coords, setCoords] = useState({
    top: -9999,
    left: -9999,
    ready: false,
  });
  const [arrowPos, setArrowPos] = useState({
    top: null,
    left: null,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const targetEl = targetRef?.current;
      const tooltipEl = tooltipRef.current;
      if (!targetEl || !tooltipEl) {
        setCoords((prev) => ({ ...prev, ready: false }));
        return;
      }

      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      const effectivePlacement =
        isMobile && (placement === 'left' || placement === 'right')
          ? 'bottom'
          : placement;

      const targetRect = targetEl.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();

      let top = 0;
      let left = 0;

      if (effectivePlacement === 'right') {
        left = targetRect.right + offset;
        top = getVerticalPosition(targetRect, tooltipRect, align);
      }

      if (effectivePlacement === 'left') {
        left = targetRect.left - tooltipRect.width - offset;
        top = getVerticalPosition(targetRect, tooltipRect, align);
      }

      if (effectivePlacement === 'top') {
        top = targetRect.top - tooltipRect.height - offset;
        left = getHorizontalPosition(targetRect, tooltipRect, align);
      }

      if (effectivePlacement === 'bottom') {
        top = targetRect.bottom + offset;
        left = getHorizontalPosition(targetRect, tooltipRect, align);
      }

      const maxLeft = window.innerWidth - tooltipRect.width - EDGE_PADDING;
      const maxTop = window.innerHeight - tooltipRect.height - EDGE_PADDING;

      const clampedTop = clamp(
        top,
        EDGE_PADDING,
        Math.max(EDGE_PADDING, maxTop),
      );
      const clampedLeft = clamp(
        left,
        EDGE_PADDING,
        Math.max(EDGE_PADDING, maxLeft),
      );

      const targetCenterX = targetRect.left + targetRect.width / 2;
      const targetCenterY = targetRect.top + targetRect.height / 2;

      let nextArrowPos = { top: null, left: null };

      if (effectivePlacement === 'top' || effectivePlacement === 'bottom') {
        nextArrowPos.left = clamp(
          targetCenterX - clampedLeft,
          ARROW_SAFE_PADDING,
          tooltipRect.width - ARROW_SAFE_PADDING,
        );
      }

      if (effectivePlacement === 'left' || effectivePlacement === 'right') {
        nextArrowPos.top = clamp(
          targetCenterY - clampedTop,
          ARROW_SAFE_PADDING,
          tooltipRect.height - ARROW_SAFE_PADDING,
        );
      }

      setCoords({
        top: clampedTop,
        left: clampedLeft,
        ready: true,
      });

      setArrowPos(nextArrowPos);
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    const ro = new ResizeObserver(() => {
      updatePosition();
    });

    if (targetRef?.current) ro.observe(targetRef.current);
    if (tooltipRef.current) ro.observe(tooltipRef.current);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      ro.disconnect();
    };
  }, [
    open,
    targetRef,
    placement,
    align,
    offset,
    title,
    description,
    buttonLabel,
  ]);

  if (!mounted || !open || !targetRef?.current) return null;

  const isMobile =
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
  const effectivePlacement =
    isMobile && (placement === 'left' || placement === 'right')
      ? 'bottom'
      : placement;

  return createPortal(
    <div
      ref={tooltipRef}
      role="dialog"
      aria-live="polite"
      className={[
        'fixed',
        'pointer-events-auto',
        'transition-opacity',
        'duration-150',
        coords.ready ? 'opacity-100' : 'opacity-0',
        className,
      ].join(' ')}
      style={{
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        maxWidth: `${maxWidth}px`,
        width: 'min(260px, calc(100vw - 24px))',
        zIndex,
      }}
    >
      <div className="relative rounded-[18px] bg-button px-4 pt-4 pb-3 text-white shadow-[0_8px_24px_rgba(15,23,42,0.24)]">
        <Arrow placement={effectivePlacement} arrowPos={arrowPos} />

        <div className="pr-2">
          {title ? <div className="font-semibold">{title}</div> : null}

          {description ? (
            <div className="mt-3 max-w-[38ch] text-sm text-white/95">
              {description}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm inline-flex min-h-[36px] min-w-[90px] items-center justify-center rounded-full border-2 border-white/90 bg-transparent px-7 font-semibold text-white transition hover:bg-white/10 active:translate-y-px"
          >
            {buttonLabel}
          </button>

          {secondaryButtonLabel && onSecondaryClick ? (
            <button
              type="button"
              onClick={onSecondaryClick}
              className="text-sm inline-flex min-h-[36px] items-center justify-center rounded-full px-3 font-semibold text-white/90 underline-offset-4 transition hover:text-white hover:underline active:translate-y-px"
            >
              {secondaryButtonLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Arrow({ placement, arrowPos }) {
  const baseStyle = {
    position: 'absolute',
    width: '16px',
    height: '16px',
    transform: 'rotate(45deg)',
    background: '#8b4112',
  };

  if (placement === 'top') {
    return (
      <span
        aria-hidden="true"
        style={{
          ...baseStyle,
          bottom: '-8px',
          left: `${arrowPos.left ?? 24}px`,
          transform: 'translateX(-50%) rotate(45deg)',
        }}
      />
    );
  }

  if (placement === 'bottom') {
    return (
      <span
        aria-hidden="true"
        style={{
          ...baseStyle,
          top: '-8px',
          left: `${arrowPos.left ?? 24}px`,
          transform: 'translateX(-50%) rotate(45deg)',
        }}
      />
    );
  }

  if (placement === 'left') {
    return (
      <span
        aria-hidden="true"
        style={{
          ...baseStyle,
          right: '-8px',
          top: `${arrowPos.top ?? 24}px`,
          transform: 'translateY(-50%) rotate(45deg)',
        }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        ...baseStyle,
        left: '-8px',
        top: `${arrowPos.top ?? 24}px`,
        transform: 'translateY(-50%) rotate(45deg)',
      }}
    />
  );
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function getVerticalPosition(targetRect, tooltipRect, align) {
  if (align === 'start') return targetRect.top;
  if (align === 'end') return targetRect.bottom - tooltipRect.height;
  return targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
}

function getHorizontalPosition(targetRect, tooltipRect, align) {
  if (align === 'start') return targetRect.left;
  if (align === 'end') return targetRect.right - tooltipRect.width;
  return targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
}
